import { useState, useEffect } from 'react'
import type { Client } from '../types'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface ClientFormProps {
  client: Client | null
  error: string
  onSave: (data: { email: string; name: string; role: string; pin?: string }) => void
  onCancel: () => void
}

export function ClientForm({ client, error, onSave, onCancel }: ClientFormProps) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<string>('client')
  const [pin, setPin] = useState('')

  useEffect(() => {
    if (client) {
      setEmail(client.email)
      setName(client.name)
      setRole(client.role || 'client')
      setPin(client.pin || '')
    } else {
      setEmail('')
      setName('')
      setRole('client')
      setPin('')
    }
  }, [client])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const data: { email: string; name: string; role: string; pin?: string } = {
      email: email.trim().toLowerCase(),
      name: name.trim(),
      role,
    }
    if (client && pin.trim()) {
      data.pin = pin.trim()
    }
    onSave(data)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="client-email">Email</Label>
          <Input
            id="client-email"
            type="email"
            required
            disabled={!!client}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="client@example.com"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="client-name">Name</Label>
          <Input
            id="client-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Client name"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {client && (
          <div className="space-y-2">
            <Label htmlFor="client-pin">PIN</Label>
            <Input
              id="client-pin"
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Leave empty to keep current"
            />
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <div className="flex gap-3 pt-2">
        <Button type="submit">
          {client ? 'Save Changes' : 'Add Client'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
